import { configureStore, type ThunkAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import appReducer from "@/features/app/appSlice";
import calibrationReducer from "@/features/calibration/calibrationSlice";
import cartographyReducer from "@/features/cartography/cartographySlice";
import configSlice from "@/features/config/configSlice";
import directionsReducer from "@/features/directions/directionsSlice";
import baseLayersEditorReducer from "@/features/editor/base_layers/baseLayersEditorSlice";
import editorReducer from "@/features/editor/editorSlice";
import findMyCarSlice from "@/features/find_my_car/findMyCarSlice";
import flightInformationReducer from "@/features/flight_information/flightInformationSlice";
import locationSlice from "@/features/location/locationSlice";
import mapReducer from "@/features/map/mapSlice";
import navigationReducer from "@/features/navigation/navigationSlice";
import sessionReducer from "@/features/session/sessionSlice";
import shareLocationReducer from "@/features/share_location/shareLocationSlice";
import shareMapReducer from "@/features/share_map/shareMapSlice";
import userPreferencesReducer from "@/features/userPreferences/userPreferencesSlice";

import cleanupSelectedFeaturesMiddleware from "@/app/middleware/cleanupSelectedFeaturesMiddleware";
import unsavedEditorChangesMiddleware from "@/app/middleware/unsavedEditorChangesMiddleware";

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});

// biome-ignore assist/source/useSortedKeys: redux store needs to be manually sorted
export const configStore = configureStore({
  reducer: {
    app: appReducer,
    baseLayersEditor: baseLayersEditorReducer,
    calibration: calibrationReducer,
    cartography: cartographyReducer,
    config: configSlice,
    directions: directionsReducer,
    editor: editorReducer,
    findMyCar: findMyCarSlice,
    flightInformation: flightInformationReducer,
    location: locationSlice,
    map: mapReducer,
    navigation: navigationReducer,
    session: sessionReducer,
    share_map: shareMapReducer,
    shareLocation: shareLocationReducer,
    userPreferences: userPreferencesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([
      unsavedEditorChangesMiddleware,
      cleanupSelectedFeaturesMiddleware,
    ]),
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

export type RootState = ReturnType<typeof configStore.getState>;
export type AppDispatch = typeof configStore.dispatch;
export type AppThunk = ThunkAction<void, RootState, unknown, any>;

export default configStore;
